'use client';

import React, { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { BenzingaEdgeProvider, EdgeCheckoutHost } from '@benzinga/edge';
import { CustomError } from '@benzinga/templates/error';

import RegisterEffects from '../root/RegisterEffects';
import { GoogleOneTap } from '../root/GoogleOneTap';
import AdditionalScripts from '../root/AdditionalScripts';
import RootTracker from '../root/RootTrackers';
import { PageStructuredData } from '../root/PageStructuredData';
import { resolvePageConfig } from '../../_libs/site-shell/resolvePageConfig';
import { RouteMetaProvider } from './RouteMetaContext';
import PageTracking from './PageTracking';
import {
  resolveDeferDisplayAdUntilReady,
  resolveDisableFooterDisplayAd,
  type PageConfig,
} from './pageConfig';
import { FreestarBootstrapWithRouteMeta } from './FreestarBootstrapWithRouteMeta';

export type { PageConfig } from './pageConfig';

interface PageProvidersProps {
  children: React.ReactNode;
  pageConfig?: PageConfig;
}

function mergePageConfig(pathnameConfig: PageConfig, pageConfig: PageConfig): PageConfig {
  return {
    ...pathnameConfig,
    ...pageConfig,
    pageTargeting: pathnameConfig.pageTargeting ?? pageConfig.pageTargeting,
    disableFooterDisplayAd:
      resolveDisableFooterDisplayAd(pathnameConfig) || resolveDisableFooterDisplayAd(pageConfig) || undefined,
    deferDisplayAdUntilReady:
      resolveDeferDisplayAdUntilReady(pathnameConfig) ?? resolveDeferDisplayAdUntilReady(pageConfig),
  };
}

export default function PageProviders({ children, pageConfig = {} }: PageProvidersProps) {
  const pathname = usePathname();
  const pathnamePageConfig = useMemo(() => resolvePageConfig(pathname), [pathname]);
  const mergedPageConfig = useMemo(
    () => mergePageConfig(pathnamePageConfig, pageConfig),
    [pathnamePageConfig, pageConfig],
  );
  const disableFooterDisplayAd = resolveDisableFooterDisplayAd(mergedPageConfig);
  const deferDisplayAdUntilReady = resolveDeferDisplayAdUntilReady(mergedPageConfig);

  return (
    <RouteMetaProvider>
      {mergedPageConfig.metaProps ? <PageStructuredData metaProps={mergedPageConfig.metaProps} /> : null}
      <RegisterEffects />
      <GoogleOneTap embeddedWidget={mergedPageConfig.embeddedWidget} />
      <RootTracker pageProps={mergedPageConfig} />
      <AdditionalScripts
        pageProps={{
          embeddedWidget: mergedPageConfig.embeddedWidget,
          disablePushPrompt: mergedPageConfig.disablePushPrompt ?? true,
        }}
      />
      <BenzingaEdgeProvider
        deferDisplayAdUntilReady={deferDisplayAdUntilReady}
        disableFooterDisplayAd={disableFooterDisplayAd}
        initialQueryParams={pageConfig.queryParams as Record<string, string> | undefined}
        initialTargeting={mergedPageConfig.pageTargeting}
      >
        <FreestarBootstrapWithRouteMeta
          disableFooterDisplayAd={disableFooterDisplayAd}
          targeting={mergedPageConfig.pageTargeting}
        />
        <EdgeCheckoutHost />
        {mergedPageConfig.error ? (
          <CustomError statusCode={mergedPageConfig.error} />
        ) : (
          <>
            {children}
            <PageTracking pageConfig={mergedPageConfig} />
          </>
        )}
      </BenzingaEdgeProvider>
    </RouteMetaProvider>
  );
}
